import { useEffect } from 'react';
import { useUiStore } from '../stores/uiStore';
import { usePipelineStore } from '../stores/pipelineStore';

// stage7/stage9 (social) retired 2026-07-05.
const STAGE_ORDER = ['new', 'stage1', 'stage1b', 'stage2', 'stage3', 'stage4', 'stage5', 'stage6', 'stage8', 'stage10', 'stage11'];

export function useKeyboardShortcuts(): void {
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const ui = useUiStore.getState();
      const target = e.target as HTMLElement;
      const tag = target.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;

      if (e.key === 'Escape') {
        if (ui.chatOpen) {
          ui.setChat(false);
          e.preventDefault();
        }
        return;
      }

      const isMeta = e.metaKey || e.ctrlKey;
      if (!isMeta) return;

      // Cmd+S always works (prevent browser save dialog)
      if (e.key === 's') {
        e.preventDefault();
        const pipeline = usePipelineStore.getState();
        if (pipeline.hasWord && pipeline._forceSave) {
          pipeline._forceSave();
        }
        return;
      }

      if (isEditable) return;

      // --- UI shortcuts ---

      if (e.key === 'b') {
        e.preventDefault();
        ui.toggleSidebar();
        return;
      }

      if (e.key === 'j') {
        e.preventDefault();
        ui.toggleChat();
        return;
      }

      // Cmd+1..4 follow the VISIBLE tab order (2026-09-12), which now starts
      // with Word Bank. Word Bank is a page rather than a workspace, so it
      // switches via setPage; the other three switch the workspace, which
      // clears activePage and keeps the four mutually exclusive.
      if (e.key === '1') { e.preventDefault(); ui.setPage('wordbank'); return; }
      if (e.key === '2') { e.preventDefault(); ui.setWorkspace('game'); return; }
      // The Social tab is hidden (2026-10-22); Cmd+3 opens Schedule, the third visible tab.
      if (e.key === '3') { e.preventDefault(); ui.setPage('schedule'); return; }
      if (e.key === '4') { e.preventDefault(); ui.setWorkspace('ads'); return; }

      // --- Pipeline shortcuts ---

      const pipeline = usePipelineStore.getState();

      if (e.key === 'g') {
        e.preventDefault();
        if (pipeline.hasWord && pipeline._generate && !pipeline.generating) {
          pipeline._generate();
        }
        return;
      }

      // Cmd+R deliberately NOT bound (2026-07-05 M16 fix): it hijacked the
      // browser-reload reflex to fire a paid regeneration. Cmd+G remains the
      // explicit generate shortcut.

      if (e.key === 'Enter') {
        e.preventDefault();
        if (pipeline.hasWord && pipeline._getApprovePayload && pipeline._approve && !pipeline.saving) {
          const payload = pipeline._getApprovePayload();
          if (payload !== null) {
            pipeline._approve(payload);
          }
        }
        return;
      }

      if (e.key === 'k') {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent('pipeline:command-palette'));
        return;
      }

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (pipeline.hasWord && pipeline._navigateStage && pipeline.currentStage) {
          const idx = STAGE_ORDER.indexOf(pipeline.currentStage);
          if (idx > 0) {
            pipeline._navigateStage(STAGE_ORDER[idx - 1]);
          }
        }
        return;
      }

      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (pipeline.hasWord && pipeline._navigateStage && pipeline.currentStage) {
          const idx = STAGE_ORDER.indexOf(pipeline.currentStage);
          const next = STAGE_ORDER[idx + 1];
          if (next && pipeline.completedStages.has(next)) {
            pipeline._navigateStage(next);
          }
        }
        return;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
}
